import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import type { CreateTagDto, Tag, TagModule } from '@ip/shared-types';
import { TAG_MODULES } from '@ip/shared-types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TagBadge } from '@/components/shared/TagBadge';
import type { AppShellContext } from '@/components/layout/AppShell';
import { t } from '@/i18n';
import { useTagsApi } from '@/services/api';
import { suggestColor } from '@/utils/suggestColor';

const COLUMN_COUNT = 3;

export function TagManagement() {
  const { nip } = useOutletContext<AppShellContext>();
  const { getTags, createTag, updateTag, deleteTag } = useTagsApi();
  const [activeModule, setActiveModule] = useState<TagModule>(TAG_MODULES[0]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [name, setName] = useState('');
  // '' = no manual pick — fall back to the computed suggestion.
  const [color, setColor] = useState<string>('');

  // Hue farthest from the tags already present in this module.
  const suggestedColor = useMemo(() => suggestColor(tags.map((t) => t.color)), [tags]);
  const effectiveColor = color || suggestedColor;

  const loadTags = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getTags(nip, activeModule);
      setTags(data);
    } catch {
      toast.error(t('errors.loadingFailed'));
    } finally {
      setLoading(false);
    }
  }, [getTags, nip, activeModule]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const resetForm = () => {
    setEditingTag(null);
    setName('');
    setColor('');
  };

  const handleModuleChange = (value: string) => {
    setActiveModule(value as TagModule);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error(t('tags.nameRequired'));
      return;
    }

    try {
      setSaving(true);

      if (editingTag) {
        await updateTag(nip, editingTag.id, { name, color: effectiveColor });
        toast.success(t('tags.updateSuccess'));
      } else {
        const dto: CreateTagDto = { module: activeModule, name, color: effectiveColor };
        await createTag(nip, dto);
        toast.success(t('tags.createSuccess'));
      }

      resetForm();
      await loadTags();
    } catch {
      toast.error(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (tag: Tag) => {
    setEditingTag(tag);
    setName(tag.name);
    setColor(tag.color);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTag(nip, id);
      toast.success(t('tags.deleteSuccess'));
      await loadTags();
    } catch {
      toast.error(t('errors.deleteFailed'));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Tabs value={activeModule} onValueChange={handleModuleChange}>
        <TabsList>
          {TAG_MODULES.map((module) => (
            <TabsTrigger key={module} value={module}>
              {t(`tags.modules.${module}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {editingTag ? t('tags.actions.edit') : t('tags.createNew')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex min-w-[220px] flex-1 flex-col gap-1.5">
                <Label htmlFor="tag-name">{t('tags.form.name')}</Label>
                <Input
                  id="tag-name"
                  placeholder={t('tags.form.namePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="tag-color">{t('tags.form.color')}</Label>
                <div className="flex items-center gap-2">
                  <input
                    id="tag-color"
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(effectiveColor) ? effectiveColor : '#000000'}
                    onChange={(e) => setColor(e.target.value)}
                    className="border-input h-9 w-10 cursor-pointer rounded border p-1"
                  />
                  <Input
                    value={effectiveColor}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-[120px]"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : editingTag ? (
                  <Pencil className="size-4" />
                ) : (
                  <Plus className="size-4" />
                )}
                {editingTag ? t('common.save') : t('common.add')}
              </Button>
              {editingTag && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  {t('common.cancel')}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('tags.existingTags')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[80px]">{t('tags.form.color')}</TableHead>
                  <TableHead>{t('tags.form.name')}</TableHead>
                  <TableHead className="w-[100px]">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 3 }, (_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={COLUMN_COUNT}>
                        <Skeleton className="h-8 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : tags.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMN_COUNT} className="h-32 text-center">
                      <p>{t('tags.noTags')}</p>
                      <p className="text-muted-foreground text-sm">
                        {t('tags.noTagsDescription')}
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  tags.map((tag) => (
                    <TableRow key={tag.id}>
                      <TableCell className="py-2">
                        <div
                          className="size-6 rounded border"
                          style={{ backgroundColor: tag.color }}
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        <TagBadge name={tag.name} color={tag.color} />
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            aria-label={t('tags.actions.edit')}
                            onClick={() => handleEdit(tag)}
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive size-8"
                                aria-label={t('tags.actions.delete')}
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {t('tags.actions.confirmDelete')}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t('tags.actions.confirmDeleteDescription')}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-white hover:bg-destructive/90"
                                  onClick={() => handleDelete(tag.id)}
                                >
                                  {t('common.delete')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
